/**
 * Push Notification Service
 *
 * Handles FCM push notifications for mobile app:
 * - Initialize push notifications on app start
 * - Request permission from user
 * - Register FCM token with backend
 * - Handle notification received (app open)
 * - Handle notification clicked (deep linking)
 * - Unregister token on logout
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * Check if push notifications are available
 * @returns {boolean}
 */
export function isPushNotificationsAvailable() {
  return Capacitor.isNativePlatform();
}

/**
 * Initialize push notifications for the current user
 * - Request permission
 * - Register device token with backend
 * - Set up notification listeners
 *
 * @param {string} userId - Current user ID
 * @returns {Promise<void>}
 */
export async function initializePushNotifications(userId) {
  if (!isPushNotificationsAvailable()) {
    console.log('[Push] Not available on this platform (web)');
    return;
  }

  console.log('[Push] Initializing push notifications for user:', userId);

  try {
    // Request permission
    const permStatus = await PushNotifications.requestPermissions();

    if (permStatus.receive !== 'granted') {
      console.warn('[Push] Permission not granted:', permStatus.receive);
      return;
    }

    console.log('[Push] Permission granted');

    // Register for push notifications
    await PushNotifications.register();

    // Set up listeners
    setupPushListeners(userId);

  } catch (error) {
    console.error('[Push] Initialization error:', error);
  }
}

/**
 * Set up push notification event listeners
 * @param {string} userId - Current user ID
 */
function setupPushListeners(userId) {
  // Called when device receives a registration token
  PushNotifications.addListener('registration', async (token) => {
    console.log('[Push] Registration token:', token.value);

    try {
      await registerDeviceToken(userId, token.value);
    } catch (error) {
      console.error('[Push] Failed to register token with backend:', error);
    }
  });

  // Called when registration fails
  PushNotifications.addListener('registrationError', (error) => {
    console.error('[Push] Registration error:', error);
  });

  // Called when notification is received while app is open
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[Push] Notification received (app open):', notification);

    // Show in-app notification toast
    // This is handled by NotificationContext via Supabase Realtime
    // We don't need to show another toast here
  });

  // Called when user taps on a notification
  PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
    console.log('[Push] Notification clicked:', notification);

    // Handle deep linking
    const data = notification.notification.data;
    handleNotificationClick(data);
  });
}

/**
 * Register device token with backend
 * @param {string} userId - User ID
 * @param {string} deviceToken - FCM device token
 * @returns {Promise<void>}
 */
async function registerDeviceToken(userId, deviceToken) {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      console.error('[Push] No session available');
      return;
    }

    const response = await fetch(`${API_URL}/notifications/register-device`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        deviceToken,
        deviceType: 'android',
        deviceName: 'Android Device' // Could get actual device name from Capacitor Device plugin
      })
    });

    if (!response.ok) {
      throw new Error('Failed to register device token');
    }

    const data = await response.json();
    console.log('[Push] Device token registered:', data);

  } catch (error) {
    console.error('[Push] Register token error:', error);
    throw error;
  }
}

/**
 * Handle notification click (deep linking)
 * @param {Object} data - Notification data
 */
function handleNotificationClick(data) {
  console.log('[Push] Handling notification click:', data);

  // Deep linking is handled by deepLinking.js utility
  // which will be called from App.js when notification is clicked

  // For now, we'll dispatch a custom event that App.js can listen to
  const event = new CustomEvent('notification-clicked', {
    detail: data
  });
  window.dispatchEvent(event);
}

/**
 * Unregister push notifications (on logout)
 * @param {string} deviceToken - FCM device token to deactivate
 * @returns {Promise<void>}
 */
export async function unregisterPushNotifications(deviceToken) {
  if (!isPushNotificationsAvailable()) {
    return;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session || !deviceToken) {
      return;
    }

    const response = await fetch(`${API_URL}/notifications/unregister-device`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        deviceToken
      })
    });

    if (!response.ok) {
      throw new Error('Failed to unregister device token');
    }

    console.log('[Push] Device token unregistered');

    // Remove all listeners
    await PushNotifications.removeAllListeners();

  } catch (error) {
    console.error('[Push] Unregister error:', error);
  }
}

/**
 * Get current device token (if available)
 * @returns {Promise<string|null>}
 */
export async function getCurrentDeviceToken() {
  if (!isPushNotificationsAvailable()) {
    return null;
  }

  try {
    // Note: Capacitor doesn't provide a direct way to get the current token
    // We need to store it when we receive the registration event
    // For now, return null - this should be improved in production
    return null;
  } catch (error) {
    console.error('[Push] Get token error:', error);
    return null;
  }
}
